import { Record as record, List } from 'immutable';
import _ from 'lodash';

import { resolveAll } from '../helpers/LinkMapper';
import { TimeTypes, JourneyDirections } from '../constants/AppConstants';

import Service from './Service';
import Fares from './Fares';

const defaults = {
    outward: new List(),
    inward: new List()
};

function getOrderForDirection (direction, search) {
    const { arriveDepart } = search.getTimebandForDirection(direction);
    return arriveDepart === TimeTypes.DEPART ? 'origin' : 'destination';
}

function getOrderedForDirection (list, orderBy) {
    const timePath = ['journey', orderBy, 'time', 'scheduledTime'];
    return list.sort((a, b) =>
        a.getIn(timePath) - b.getIn(timePath) || a.getTripDuration() - b.getTripDuration()
    );
}

function getServiceFilter (trip) {
    return (service) => !service.isDisabledForTrip(trip);
}

function getSingleFaresFromServiceList (services, trip) {
    let fares = new List();
    services.filter(getServiceFilter(trip)).forEach((service)=> {
        fares = fares.concat(service.fares.getSingleFares(!trip.isReturn()));
    });

    return fares.toOrderedSet().toList().sortBy(fare => fare.getPrice());
}

function getReturnFaresFromServiceList (services, trip) {
    let fares = new List();
    services.filter(getServiceFilter(trip)).forEach((service)=> {
        fares = fares.concat(service.fares.getReturnFares());
    });

    return fares.toOrderedSet().toList().sortBy(fare => fare.getPrice());
}

// price ascending; the journey-planner-designated cheapest fare wins a price tie
function byPriceCheapestFirst (trip) {
    return (a, b) =>
        a.getPrice() - b.getPrice() ||
        (trip.isCheapestFare(b) ? 1 : 0) - (trip.isCheapestFare(a) ? 1 : 0);
}

class Services extends record(defaults) {

    static fromJSON (json) {
        return new Services({
            outward: resolveAll(json.outward).as(Service),
            inward: resolveAll(json.inward).as(Service)
        });
    }

    orderForSearch (search) {
        return this
            .set('outward', getOrderedForDirection(this.outward, getOrderForDirection(JourneyDirections.OUTWARD, search)))
            .set('inward', getOrderedForDirection(this.inward, getOrderForDirection(JourneyDirections.INWARD, search)));
    }

    getServicesForDirection(
        direction,
        outboundFare,
        outboundSplit
    ) {
        if (outboundSplit) {
            const fixedSplit =
                direction === JourneyDirections.INWARD && outboundSplit?.isReturn?.();

            return this[direction].filter(
                (service) => !fixedSplit || service.isValidWithSplit(outboundSplit)
            );
        } else {
            const fixedFare =
                direction === JourneyDirections.INWARD && outboundFare?.isReturn?.();

            return this[direction].filter(
                (service) => !fixedFare || service.isValidWithFare(outboundFare)
            );
        }
    }

    getList(direction, ticketType = TimeTypes.DEPART, outboundFare, outboundSplit) {
        const orderByField = ticketType === TimeTypes.DEPART
            ? (service) => service.journey.getDepartureDateTime(false).toDate()
            : (service) => service.journey.getArrivalDateTime(false).toDate();

        return _.orderBy(
            this.getServicesForDirection(direction, outboundFare, outboundSplit),
            orderByField,
            'asc'
        );
    }

    getAllFares (trip, includeFirstClass = false) {
        let fares = this.getAllSingleFares(trip)
            .concat(this.getAllReturnFares(trip));

        if (!trip.filters.showStandard) {
            fares = fares.filterNot(Fares.isStandard);
        }

        if (!trip.filters.showFirstClass && !includeFirstClass) {
            fares = fares.filterNot(Fares.isFirstClass);
        }

        return fares;
    }

    getAllSingleFares (trip) {
        return this.getAllSingleOutwardFares(trip)
            .concat(this.getAllSingleInwardFares(trip))
            .groupBy(fare => fare.id).toList().map(x => x.first())
            .sort(byPriceCheapestFirst(trip));
    }

    getAllReturnFares (trip) {
        return this.getAllReturnOutwardFares(trip)
            .concat(this.getAllReturnInwardFares(trip))
            .groupBy(fare => fare.id).toList().map(x => x.first())
            .sort(byPriceCheapestFirst(trip));
    }

    getAllSingleOutwardFares (trip) {
        return getSingleFaresFromServiceList(this.outward, trip);
    }

    getAllSingleInwardFares (trip) {
        return getSingleFaresFromServiceList(this.inward, trip);

    }

    getAllReturnOutwardFares (trip) {
        return getReturnFaresFromServiceList(this.outward, trip);
    }

    getAllReturnInwardFares (trip) {
        return getReturnFaresFromServiceList(this.inward, trip);
    }

    getLatestArrivingService (journeyDirection) {
        return this.getServicesForDirection(journeyDirection).last();
    }

    getEarliestLeavingService (journeyDirection) {
        return this.getServicesForDirection(journeyDirection).first();
    }

    getAllReturnTravelcards (trip) {
        return this.getAllReturnFares(trip).filter((item) => item.ticketType.isReturnTravelcard());
    }

    hasDisruptedServices () {
        const disruptedServices = this.outward.concat(this.inward)
            .filter((service) => service.isDisrupted());
        return !disruptedServices.isEmpty();
    }

    hasServices () {
        return !!this.outward.concat(this.inward).count();
    }

    hasServicesInFuture () {
        const services = this.outward.concat(this.inward);
        const servicesInFuture = services.filter((service) => {
            const departureDateTime = service.getDepartureDateTime();
            return departureDateTime && departureDateTime.isAfter(Date.now());
        });
        return !!servicesInFuture.count();
    }

    hasReturnTravelcards(trip) {
        return !!this.getAllReturnTravelcards(trip).count();
    }

    hasCollectableServices (trip) {
        const collectableServices = this.outward.filter((service) => {
            return trip.isSmartcard() ? service.isCollectableSmartcard() : service.isCollectable();
        });
        return !!collectableServices.count();
    }

    isEarlierDisabled (direction) {
        if (direction === JourneyDirections.OUTWARD) {
            return false;
        }
        const latestArrivingService = this.getLatestArrivingService(JourneyDirections.OUTWARD);
        const earliestLeavingService = this.getEarliestLeavingService(JourneyDirections.INWARD);

        if (!latestArrivingService || !earliestLeavingService) {
            return true;
        }
        //Adding 50 minutes padding to avoid unpredictable JP response
        return latestArrivingService.getArrivalDateTime().add(50, 'minutes').isAfter(earliestLeavingService.getDepartureDateTime());
    }

    isLaterDisabled (trip, direction) {
        if (direction === JourneyDirections.INWARD || !trip.isReturn()) {
            return false;
        }
        const latestArrivingService = this.getLatestArrivingService(JourneyDirections.OUTWARD);
        const earliestLeavingService = this.getEarliestLeavingService(JourneyDirections.INWARD);

        if (!latestArrivingService || !earliestLeavingService) {
            return true;
        }

        return latestArrivingService.getArrivalDateTime().isAfter(earliestLeavingService.getDepartureDateTime());
    }

    getFareById (trip, id, includeFirstClass) {
        if (id) {
            const addFares = this.getAllFares(trip, includeFirstClass);
            return addFares.find((fare) => fare.id === id);
        }

        return null;
    }

    hasServicesWithBestSplitSavings() {
        return !!this.getBestSplitSavingForDirection(JourneyDirections.OUTWARD) ||
            !!this.getBestSplitSavingForDirection(JourneyDirections.INWARD);
    }

    getBestSplitSavingForDirection(direction) {
        const services = this[direction];
        return Math.max(...(services && services.map((service) => service.getBestSplitSaving(direction)).toArray() || []), 0);
    }
}


export default Services;
